/**
 * Pristine Water Systems quote CTA + request modal — Phase 2J-A.
 *
 * Mounted on the customer Passport property page. Visibility gated by the
 * `pristine_quote_cta` feature flag AND the presence of any recommendations
 * for the property (no point asking for a quote on a clean test).
 *
 * Wording per spec: NO absolute guarantees, "help improve" / "assist in
 * addressing the recommendations" framing only.
 */
import { useState } from "react";
import { ArrowRight, Droplets, X, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useFeature } from "@/lib/useFeature";


export function PristineQuoteCTA({ propertyId, hasRecommendations, dataTestId = "pristine-cta" }) {
  const { isVisible } = useFeature("pristine_quote_cta");
  const [open, setOpen] = useState(false);
  if (!isVisible || !hasRecommendations) return null;

  return (
    <>
      <div data-testid={dataTestId} className="rounded-3xl bg-gradient-to-br from-cyan-900 via-slate-900 to-slate-800 text-white p-6 lg:p-8">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-12 h-12 rounded-2xl bg-cyan-500/20 flex items-center justify-center flex-shrink-0">
            <Droplets className="w-6 h-6 text-cyan-300" />
          </div>
          <div className="flex-1 min-w-[260px]">
            <div className="text-[11px] uppercase tracking-[0.2em] font-bold text-cyan-300">Pristine Water Systems</div>
            <h3 className="font-display text-xl lg:text-2xl font-bold mt-1.5">Request a tailored treatment quote</h3>
            <p className="mt-2 text-sm text-slate-300 leading-relaxed max-w-2xl">
              Based on your water test results and recommendations, Pristine Water Systems can prepare a tailored quote
              for the equipment and services required to help improve your water quality.
            </p>
          </div>
          <Button
            onClick={() => setOpen(true)}
            data-testid="pristine-cta-open"
            className="rounded-full bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-semibold px-5 py-5"
          >
            Request Quote / Proposal <ArrowRight className="w-4 h-4 ml-1.5" />
          </Button>
        </div>
      </div>
      {open && <PristineQuoteModal propertyId={propertyId} onClose={() => setOpen(false)} />}
    </>
  );
}


function PristineQuoteModal({ propertyId, onClose }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", preferred_contact: "phone", preferred_time: "", notes: "" });
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(null);

  const submit = async (e) => {
    e?.preventDefault?.();
    setSubmitting(true);
    try {
      const { data } = await api.post("/pristine/quote-requests", { property_id: propertyId, ...form });
      setSuccess(data);
      toast.success("Quote request received");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not submit request");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[60]" data-testid="pristine-quote-modal">
      <div className="absolute inset-0 bg-slate-900/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative max-w-2xl mx-auto mt-12 mb-8 bg-white rounded-3xl shadow-2xl">
        <div className="flex items-center justify-between gap-3 px-6 py-4 border-b border-slate-100">
          <div>
            <div className="text-[10px] uppercase tracking-wider font-bold text-cyan-700">Pristine Water Systems</div>
            <h3 className="font-display text-lg font-bold text-slate-900 mt-0.5">
              {success ? "Request received" : "Request your recommended water treatment quote"}
            </h3>
          </div>
          <button onClick={onClose} data-testid="pristine-modal-close" className="w-9 h-9 inline-flex items-center justify-center rounded-lg text-slate-600 hover:bg-slate-100">
            <X className="w-4 h-4" />
          </button>
        </div>
        {success ? (
          <div className="p-8 text-center">
            <div className="mx-auto w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center">
              <Check className="w-7 h-7 text-emerald-600" />
            </div>
            <p className="mt-4 text-sm text-slate-700 leading-relaxed max-w-md mx-auto">
              Your quote request has been received. A Pristine Water Systems specialist will review your water test
              results and prepare a tailored recommendation for your property.
            </p>
            <p className="mt-3 text-xs text-slate-500">Reference: <code className="font-mono">{success.id.slice(0, 8).toUpperCase()}</code></p>
            <Button onClick={onClose} data-testid="pristine-success-close" className="mt-6 rounded-full">Done</Button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 py-5 space-y-4">
            <p className="text-xs text-slate-500 leading-relaxed">
              We've pre-attached your test results, recommendations and property details so your specialist has
              everything they need. Any additional context below helps tailor the proposal further.
            </p>
            <div className="grid sm:grid-cols-2 gap-3">
              <Field label="Preferred contact" name="preferred_contact" value={form.preferred_contact}
                onChange={(v) => setForm((x) => ({ ...x, preferred_contact: v }))}
                select options={[["phone","Phone"],["email","Email"],["sms","SMS"]]} testid="pristine-contact" />
              <Field label="Best time to call" name="preferred_time" value={form.preferred_time}
                onChange={(v) => setForm((x) => ({ ...x, preferred_time: v }))} placeholder="e.g. weekday mornings"
                testid="pristine-time" />
            </div>
            <Field label="Anything else we should know? (Optional)" name="notes" value={form.notes}
              onChange={(v) => setForm((x) => ({ ...x, notes: v }))} textarea rows={3}
              placeholder="Existing systems, concerns, urgency, access notes…" testid="pristine-notes" />
            <p className="text-[11px] text-slate-500 leading-relaxed">
              We will get in touch shortly to discuss suitable Pristine Water Systems treatment options that can help
              return your water quality to an acceptable level for your intended use.
            </p>
            <div className="flex items-center justify-end gap-2 pt-2">
              <Button type="button" onClick={onClose} variant="outline" className="rounded-full">Cancel</Button>
              <Button type="submit" disabled={submitting} data-testid="pristine-submit-btn"
                className="rounded-full bg-cyan-600 hover:bg-cyan-500 text-white px-5">
                {submitting ? "Sending…" : "Send Quote Request"} <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}


function Field({ label, name, value, onChange, placeholder, textarea, rows, select, options, testid }) {
  if (select) {
    return (
      <label className="block">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        <select data-testid={testid} name={name} value={value} onChange={(e) => onChange(e.target.value)}
          className="mt-1.5 w-full h-11 rounded-xl border border-slate-300 px-3 text-sm bg-white">
          {(options || []).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
        </select>
      </label>
    );
  }
  if (textarea) {
    return (
      <label className="block">
        <span className="text-xs font-semibold text-slate-700">{label}</span>
        <textarea data-testid={testid} name={name} value={value} onChange={(e) => onChange(e.target.value)}
          rows={rows || 3} placeholder={placeholder}
          className="mt-1.5 w-full rounded-xl border border-slate-300 px-3 py-2 text-sm" />
      </label>
    );
  }
  return (
    <label className="block">
      <span className="text-xs font-semibold text-slate-700">{label}</span>
      <input data-testid={testid} name={name} value={value} onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="mt-1.5 w-full h-11 rounded-xl border border-slate-300 px-3 text-sm" />
    </label>
  );
}
